import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { BarChart3, Activity, Building2, Calendar, Coins, DollarSign, Euro, PoundSterling, SwissFranc, Gem, Wallet, ArrowLeftRight, TrendingUp, Mountain } from 'lucide-react';
import { XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Area, AreaChart, LineChart, Line, ReferenceLine } from 'recharts';
import apiClient from '@services/api';
import { token, seriesColor, baselineOffset } from '../styles/tokens';
import SEO from '@components/SEO';
import SectionHeader from '@components/dashboard/SectionHeader';
import PriceChange from '@components/dashboard/PriceChange';
import MarketCard from '@components/dashboard/MarketCard';
import PriceTicker, { TickerItem } from '@components/dashboard/PriceTicker';
import RangeSelector from '@components/dashboard/RangeSelector';
import MarketStrip, { StripEntry } from '@components/dashboard/MarketStrip';
import MarketMovers from '@components/dashboard/MarketMovers';
import ForumHighlights from '@components/dashboard/ForumHighlights';
import StatTile from '@components/dashboard/StatTile';
import CurrencyConverter from '@components/dashboard/CurrencyConverter';
import LatestNews from '@components/dashboard/LatestNews';
import { getInstrumentCurrencySymbol } from '@utils/helpers';
import { newestTimestamp, dataAgeMs as dataAge, isDataLive as isLive } from '@utils/dataFreshness';

interface MarketData {
  symbol: string;
  name: string;
  price: number;
  change: number;
  changePercent: number;
  timestamp: string;
}

interface IndexData {
  symbol: string;
  name: string;
  price: number;
  change: number;
  changePercent: number;
  timestamp: string;
}

interface TurkishGoldData {
  symbol: string;
  name: string;
  price: number;
  change: number;
  changePercent: number;
  purity: number;
  weight_grams: number;
  timestamp: string;
}

interface ChartDataPoint {
  timestamp: string;
  date?: string;
  price: number;
}

interface StockStats {
  total_bist_stocks: number;
  total_us_stocks: number;
  latest_data_date: string | null;
}

type TimeRange = '1h' | '3h' | '1d' | '3d' | '1w' | '1m' | '3m' | '6m' | '1y' | 'all';

const TIME_RANGES: { value: TimeRange; label: string }[] = [
  { value: '1h', label: '1S' },
  { value: '3h', label: '3S' },
  { value: '1d', label: '1G' },
  { value: '3d', label: '3G' },
  { value: '1w', label: '1H' },
  { value: '1m', label: '1A' },
  { value: '3m', label: '3A' },
  { value: '6m', label: '6A' },
  { value: '1y', label: '1Y' },
  { value: 'all', label: 'Tümü' },
];

type ChartType = 'line' | 'area' | 'baseline';

const CHART_TYPES: { value: ChartType; label: string; Icon: typeof TrendingUp }[] = [
  { value: 'line', label: 'Çizgi', Icon: TrendingUp },
  { value: 'area', label: 'Alan', Icon: Mountain },
  { value: 'baseline', label: 'Taban', Icon: BarChart3 },
];

// All URLs verified 200 OK (2026-07)
const BANKS = [
  { name: 'Ziraat Bankası', url: 'https://www.ziraatbank.com.tr/tr/fiyatlar-ve-oranlar' },
  { name: 'İş Bankası', url: 'https://www.isbank.com.tr/doviz-kurlari' },
  { name: 'Garanti BBVA', url: 'https://www.garantibbva.com.tr/doviz-kurlari' },
  { name: 'Akbank', url: 'https://www.akbank.com/doviz-kurlari' },
  { name: 'Halkbank', url: 'https://www.halkbank.com.tr/tr/yatirim/para-piyasalari/doviz-islemleri' },
  { name: 'Vakıfbank', url: 'https://www.vakifbank.com.tr/doviz-kurlari.aspx' },
  { name: 'TCMB', url: 'https://www.tcmb.gov.tr/kurlar/today.xml' },
  { name: 'Denizbank', url: 'https://www.denizbank.com/yatirim/piyasalar/doviz' },
];

const ImprovedDashboard: React.FC = () => {
  const navigate = useNavigate();

  // State
  const [marketData, setMarketData] = useState<MarketData[]>([]);
  const [turkishGold, setTurkishGold] = useState<TurkishGoldData[]>([]);
  const [selectedSymbol, setSelectedSymbol] = useState('USDTRY=X');
  const [chartData, setChartData] = useState<ChartDataPoint[]>([]);
  const [selectedName, setSelectedName] = useState('USD/TRY');
  const [stockStats, setStockStats] = useState<StockStats | null>(null);
  const [selectedRange, setSelectedRange] = useState<TimeRange>('1d');
  const [chartType, setChartType] = useState<ChartType>('area');
  const [indicesData, setIndicesData] = useState<IndexData[]>([]);

  const [sparklines, setSparklines] = useState<Record<string, number[]>>({});
  const [loading, setLoading] = useState(true);
  const [chartLoading, setChartLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Recent price series per symbol, for the mini charts on the market cards.
  // One batched request for the whole grid rather than one per card — the
  // backend resolves all of them in a single query.
  const fetchSparklines = async (symbols: string[]) => {
    if (symbols.length === 0) return;
    try {
      const response = await apiClient.get<{ series: Record<string, number[]> }>(
        `/financial/trending/sparklines/?symbols=${encodeURIComponent(symbols.join(','))}`
      );
      setSparklines(prev => ({ ...prev, ...(response.data.series || {}) }));
    } catch (err) {
      // A missing sparkline degrades the card, it doesn't break it — the
      // price and change chip still carry the actual values.
      console.error('Error fetching sparklines:', err);
    }
  };

  // Fetch all market data (currencies, gold, silver)
  const fetchMarketData = async () => {
    try {
      const response = await apiClient.get<{ currencies: MarketData[] }>('/financial/trending/currencies/');
      if (response.data.currencies) {
        setMarketData(response.data.currencies);
        if (!selectedSymbol && response.data.currencies.length > 0) {
          setSelectedSymbol(response.data.currencies[0].symbol);
          setSelectedName(response.data.currencies[0].name);
        }
      }
    } catch (err) {
      console.error('Error fetching market data:', err);
      setMarketData([]);
    }
  };

  // Fetch indices data
  const fetchIndices = async () => {
    try {
      const response = await apiClient.get<{ indices: IndexData[] }>('/financial/trending/indices/');
      if (response.data.indices) {
        setIndicesData(response.data.indices);
      }
    } catch (err) {
      console.error('Error fetching indices:', err);
      setIndicesData([]);
    }
  };

  // Fetch Turkish gold prices
  const fetchTurkishGold = async () => {
    try {
      const response = await apiClient.get<{ turkish_gold: TurkishGoldData[] }>('/financial/trending/turkish_gold/');
      if (response.data.turkish_gold) {
        const mainGoldTypes = ['gram_altin', 'ceyrek_altin', 'yarim_altin', 'tam_altin'];
        const filteredGold = response.data.turkish_gold.filter(g => mainGoldTypes.includes(g.symbol));
        setTurkishGold(filteredGold);
      }
    } catch (err) {
      console.error('Error fetching Turkish gold:', err);
      setTurkishGold([]);
    }
  };

  // Fetch stock stats
  const fetchStockStats = async () => {
    try {
      const response = await apiClient.get<StockStats>('/financial/trending/stock_stats/');
      if (response.data) {
        setStockStats(response.data);
      }
    } catch (err) {
      console.error('Error fetching stock stats:', err);
    }
  };

  // Fetch chart data for selected symbol with time range
  const fetchChartData = async (symbol: string, range: TimeRange = '1d') => {
    if (!symbol) return;
    setChartLoading(true);
    try {
      const response = await apiClient.get<{ data: ChartDataPoint[], name: string, range: string }>(`/financial/trending/chart_history/?symbol=${encodeURIComponent(symbol)}&range=${range}`);
      if (response.data.data && response.data.data.length > 0) {
        setChartData(response.data.data);
        setSelectedName(response.data.name);
      } else {
        setChartData([]);
      }
    } catch (err) {
      console.error('Error fetching chart data:', err);
      setChartData([]);
    } finally {
      setChartLoading(false);
    }
  };

  // Initial load
  useEffect(() => {
    const loadDashboard = async () => {
      setLoading(true);
      try {
        await Promise.all([
          fetchMarketData(),
          fetchTurkishGold(),
          fetchStockStats(),
          fetchIndices(),
        ]);
      } catch (err) {
        setError('Veriler yüklenirken hata oluştu');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    loadDashboard();

    // Auto refresh every 60 seconds
    const interval = setInterval(() => {
      fetchMarketData();
      fetchTurkishGold();
      fetchStockStats();
      fetchIndices();
    }, 60000);

    return () => clearInterval(interval);
  }, []);

  // Sparklines follow whichever symbols are on screen, in one request. Kept
  // out of the main load so a slow or failing mini-chart fetch never delays
  // the prices themselves.
  useEffect(() => {
    const symbols = [
      ...marketData.map(d => d.symbol),
      ...indicesData.map(d => d.symbol),
    ];
    if (symbols.length > 0) fetchSparklines(symbols);
  }, [marketData.map(d => d.symbol).join(','), indicesData.map(d => d.symbol).join(',')]);

  // Reload chart when symbol or range changes
  useEffect(() => {
    if (selectedSymbol) {
      fetchChartData(selectedSymbol, selectedRange);
    }
  }, [selectedSymbol, selectedRange]);

  const handleSelect = (symbol: string, name?: string) => {
    setSelectedSymbol(symbol);
    if (name) setSelectedName(name);
  };

  const handleDetailClick = (symbol: string) => {
    navigate(`/instrument/${encodeURIComponent(symbol)}`);
  };

  const formatPrice = (price: number, symbol: string) => {
    const currencySymbol = getInstrumentCurrencySymbol(symbol);
    // Metals get grouped 2-digit prices; toFixed(4) is for currency rates.
    if (symbol.includes('GC=F') || symbol.includes('SI=F') || /altin|bilezik/i.test(symbol)) {
      return `${currencySymbol}${price.toLocaleString('en-US', { maximumFractionDigits: 2 })}`;
    }
    return `${currencySymbol}${price.toFixed(4)}`;
  };

  const formatChartTime = (timestamp: string) => {
    const date = new Date(timestamp);
    if (selectedRange === '1h' || selectedRange === '3h' || selectedRange === '1d') {
      return date.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' });
    } else if (selectedRange === '3d') {
      return date.toLocaleString('tr-TR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
    } else if (selectedRange === '1w') {
      return date.toLocaleDateString('tr-TR', { weekday: 'short', day: '2-digit', month: 'short' });
    } else if (selectedRange === '1m' || selectedRange === '3m') {
      return date.toLocaleDateString('tr-TR', { day: '2-digit', month: 'short' });
    } else {
      return date.toLocaleDateString('tr-TR', { month: 'short', year: '2-digit' });
    }
  };

  // Gold instruments — GC=F is the ons futures contract, the rest are the
  // Turkish physical forms. Silver (SI=F) is deliberately excluded: it gets
  // the grey Gem icon, so tinting its tile gold would misreport the metal.
  const isGold = (symbol: string) =>
    symbol.includes('GC=F') || /altin/i.test(symbol);

  const getIcon = (symbol: string) => {
    if (symbol.includes('GC=F')) return <Coins className="w-4 h-4 text-gold-400" />;
    if (symbol.includes('SI=F')) return <Gem className="w-4 h-4 text-gray-300" />;
    if (symbol.includes('USD')) return <DollarSign className="w-4 h-4 text-accent-400" />;
    if (symbol.includes('EUR')) return <Euro className="w-4 h-4 text-accent-400" />;
    if (symbol.includes('GBP')) return <PoundSterling className="w-4 h-4 text-accent-400" />;
    if (symbol.includes('CHF')) return <SwissFranc className="w-4 h-4 text-accent-400" />;
    if (symbol === 'gram_altin' || symbol === 'ceyrek_altin' || symbol === 'yarim_altin' || symbol === 'tam_altin') {
      return <Coins className="w-4 h-4 text-gold-400" />;
    }
    return <Wallet className="w-4 h-4 text-accent-400" />;
  };

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-lg font-semibold text-down-400 mb-1">Hata</h2>
          <p className="text-gray-500 text-sm">{error}</p>
        </div>
      </div>
    );
  }

  // Tape items
  const tickerItems: TickerItem[] = [];
  if (!loading) {
    const usdtry = marketData.find(d => d.symbol === 'USDTRY=X');
    if (usdtry) tickerItems.push({ key: 'usdtry', icon: <DollarSign className="w-3.5 h-3.5 text-accent-400" />, label: 'USD/TRY', price: formatPrice(usdtry.price, usdtry.symbol), changePercent: usdtry.changePercent, onClick: () => handleSelect(usdtry.symbol, usdtry.name) });
    const eurusd = marketData.find(d => d.symbol === 'EURUSD=X');
    if (eurusd) tickerItems.push({ key: 'eurusd', icon: <ArrowLeftRight className="w-3.5 h-3.5 text-accent-400" />, label: 'EUR/USD', price: eurusd.price.toFixed(4), changePercent: eurusd.changePercent, onClick: () => handleSelect(eurusd.symbol, eurusd.name) });
    const goldOns = marketData.find(d => d.symbol === 'GC=F');
    if (goldOns) tickerItems.push({ key: 'gold', icon: <Coins className="w-3.5 h-3.5 text-gold-400" />, label: 'Ons Altın', price: formatPrice(goldOns.price, goldOns.symbol), changePercent: goldOns.changePercent, onClick: () => handleSelect('GC=F', 'Altın (USD)') });
    const gram = turkishGold.find(g => g.symbol === 'gram_altin');
    if (gram) tickerItems.push({ key: 'gram', icon: <Coins className="w-3.5 h-3.5 text-gold-400" />, label: 'Gram Altın', price: `₺${gram.price.toLocaleString('tr-TR', { maximumFractionDigits: 0 })}`, changePercent: gram.changePercent, onClick: () => navigate('/altin') });
  }

  // Gold strip entries
  const goldEntries: StripEntry[] = turkishGold.map(g => ({
    key: g.symbol,
    icon: getIcon(g.symbol),
    name: g.name,
    price: `₺${g.price.toLocaleString('tr-TR', { maximumFractionDigits: 0 })}`,
    changePercent: g.changePercent,
    meta: `${g.weight_grams}g · %${(g.purity * 100).toFixed(1)} saflık`,
  }));

  // Open sessions tick every few minutes; 90 quiet minutes means the exchange
  // is closed, and a flatline + 0.00% would read as broken data instead
  const closedLabel = (timestamp: string): string | undefined => {
    const age = Date.now() - new Date(timestamp).getTime();
    if (age < 90 * 60 * 1000) return undefined;
    return new Date(timestamp).toLocaleTimeString('tr-TR', {
      timeZone: 'Europe/Istanbul', hour: '2-digit', minute: '2-digit',
    });
  };

  // Indices strip entries
  const venueCode = (symbol: string) =>
    symbol === 'XU100.IS' ? 'BIST'
      : symbol.includes('N225') ? 'TYO'
      : symbol.includes('FTSE') ? 'LSE'
      : symbol.includes('GDAXI') ? 'FRA'
      : symbol.includes('IXIC') ? 'NASDAQ'
      : 'NYSE';

  const indexEntries: StripEntry[] = indicesData.map(idx => ({
    key: idx.symbol,
    icon: <span className="badge">{venueCode(idx.symbol)}</span>,
    name: idx.name,
    price: idx.price.toLocaleString('en-US', { maximumFractionDigits: 2 }),
    changePercent: idx.changePercent,
    sparkline: sparklines[idx.symbol],
    closedAt: closedLabel(idx.timestamp),
  }));

  // Chart stats
  const chartStats = (() => {
    if (chartData.length === 0) return null;
    const prices = chartData.map(d => d.price);
    const currentPrice = prices[prices.length - 1];
    const openPrice = prices[0];
    const changePct = openPrice > 0 ? ((currentPrice - openPrice) / openPrice) * 100 : 0;
    return {
      currentPrice,
      changePct,
      minPrice: Math.min(...prices),
      maxPrice: Math.max(...prices),
    };
  })();

  // The chart series takes its colour from the same number the PriceChange
  // chip above it displays, so the two always tell the same story.
  const SERIES = seriesColor(chartStats?.changePct ?? 0);
  // Fraction of the vertical range sitting above the opening price, used to
  // split the baseline chart's fill into gain and loss regions.
  const baselineSplit = baselineOffset(chartData.map(d => d.price), chartData[0]?.price ?? 0);

  const latestTimestamp = newestTimestamp(marketData);
  const isDataLive = isLive(dataAge(latestTimestamp));
  const isSameDay = latestTimestamp
    ? new Date(latestTimestamp).toDateString() === new Date().toDateString()
    : true;

  return (
    <div className="min-h-screen">
      <SEO
        title="Dolar Kuru, Altın Fiyatı, BIST 100"
        description="Canlı dolar kuru, euro kuru, gram altın, çeyrek altın fiyatları. BIST 100, Apple, Tesla, Nvidia hisse takibi. Ücretsiz portföy yönetimi."
        keywords="dolar kaç tl, euro kaç tl, gram altın fiyatı, çeyrek altın, BIST 100, borsa istanbul, Apple hisse, Tesla hisse"
        canonical="/"
        breadcrumbs={[{ name: "Ana Sayfa", url: "/" }]}
      />

      <PriceTicker items={tickerItems} />

      <div className="container-financial py-6">

        {/* Page header */}
        <div className="flex items-end justify-between mb-6">
          <div>
            <h1 className="text-xl font-semibold tracking-tight text-white">Piyasalar</h1>
            <p className="text-sm text-gray-500 mt-0.5">Döviz, altın ve endekslerin canlı görünümü</p>
          </div>
          {!loading && marketData.length > 0 && (
            <div className="flex items-center gap-2 text-num text-[11px] text-gray-500" title="Veriler 60 saniyede bir yenilenir">
              <span className={isDataLive ? 'live-dot' : 'live-dot-stale'}></span>
              <span className="font-medium text-gray-400">{isDataLive ? 'CANLI' : 'GECİKMELİ'}</span>
              <span className="text-gray-700">·</span>
              <span>
                {isSameDay
                  ? new Date(latestTimestamp!).toLocaleTimeString('tr-TR')
                  : new Date(latestTimestamp!).toLocaleString('tr-TR', { dateStyle: 'medium', timeStyle: 'short' })}
              </span>
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">

          {/* Main column */}
          <div className="lg:col-span-8 space-y-8">

            {/* Chart */}
            <section>
              <div className="card p-4 sm:p-5">
                <div className="flex flex-wrap items-start justify-between gap-x-6 gap-y-3 mb-4">
                  <div className="min-w-0">
                    <div className="text-sm text-gray-500 mb-1 truncate">{selectedName}</div>
                    {chartStats ? (
                      <div className="flex items-baseline gap-2.5 flex-wrap">
                        {/* The page's one hero moment - everything else stays on the small steps */}
                        <span className="text-num text-3xl sm:text-[2.125rem] font-semibold text-white tracking-tight">
                          {formatPrice(chartStats.currentPrice, selectedSymbol)}
                        </span>
                        <PriceChange value={chartStats.changePct} />
                      </div>
                    ) : (
                      <div className="text-num text-3xl sm:text-[2.125rem] text-gray-700">—</div>
                    )}
                    {chartStats && (
                      <div className="text-num text-[11px] text-gray-600 mt-1.5">
                        Düşük <span className="text-down-400">{formatPrice(chartStats.minPrice, selectedSymbol)}</span>
                        <span className="mx-1.5 text-gray-700">·</span>
                        Yüksek <span className="text-up-400">{formatPrice(chartStats.maxPrice, selectedSymbol)}</span>
                      </div>
                    )}
                  </div>
                  {/* Never flex-shrink-0: these controls are ~670px wide, so at
                      any viewport narrower than that refusing to shrink puts the
                      whole page into horizontal scroll. Below sm they wrap; from
                      sm up the row shrinks and the range selector — the only
                      child without flex-shrink-0 — absorbs it by scrolling
                      inside itself. */}
                  <div className="flex flex-wrap items-center gap-3 min-w-0 w-full sm:w-auto sm:flex-nowrap">
                    <div className="segmented flex-shrink-0">
                      {CHART_TYPES.map((ct) => (
                        <button
                          key={ct.value}
                          onClick={() => setChartType(ct.value)}
                          className={`segmented-item ${chartType === ct.value ? 'segmented-item-active' : ''}`}
                          title={ct.label}
                        >
                          <ct.Icon className="w-3.5 h-3.5 inline -mt-0.5 mr-1" />
                          {ct.label}
                        </button>
                      ))}
                    </div>
                    <RangeSelector
                      options={TIME_RANGES}
                      value={selectedRange}
                      onChange={setSelectedRange}
                      className="order-last w-full sm:order-none sm:w-auto"
                    />
                    <button
                      onClick={() => handleDetailClick(selectedSymbol)}
                      className="text-xs font-medium text-accent-400 hover:text-accent-300 transition-colors whitespace-nowrap flex-shrink-0 ml-auto sm:ml-0"
                    >
                      Detay →
                    </button>
                  </div>
                </div>

                {chartLoading ? (
                  <div className="h-72 flex items-center justify-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-2 border-white/10 border-t-accent-400"></div>
                  </div>
                ) : chartData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={288}>
                    {chartType === 'line' ? (
                      <LineChart data={chartData} margin={{ top: 4, right: 0, bottom: 0, left: 0 }}>
                        <CartesianGrid stroke="rgba(255,255,255,0.05)" vertical={false} />
                        <XAxis
                          dataKey="timestamp"
                          tickFormatter={formatChartTime}
                          stroke={token("chart-axis")}
                          tick={{ fontSize: 10, fontFamily: 'JetBrains Mono' }}
                          tickLine={false}
                          axisLine={false}
                          interval="equidistantPreserveStart"
                          minTickGap={48}
                        />
                        <YAxis
                          stroke={token("chart-axis")}
                          tick={{ fontSize: 10, fontFamily: 'JetBrains Mono' }}
                          tickLine={false}
                          axisLine={false}
                          width={64}
                          domain={['auto', 'auto']}
                          tickFormatter={(value) => value.toFixed(2)}
                        />
                        <Tooltip
                          contentStyle={{
                            backgroundColor: token("surface-raised"),
                            border: '1px solid rgba(255,255,255,0.1)',
                            borderRadius: '10px',
                            fontSize: '12px',
                            fontFamily: 'JetBrains Mono',
                          }}
                          labelStyle={{ color: token("chart-axis"), marginBottom: '2px' }}
                          labelFormatter={(label) => new Date(label).toLocaleString('tr-TR')}
                          formatter={(value: number | undefined) => [formatPrice(value ?? 0, selectedSymbol), selectedName]}
                        />
                        <Line
                          type="monotone"
                          dataKey="price"
                          stroke={SERIES}
                          strokeWidth={2}
                          dot={false}
                          activeDot={{ r: 4, fill: SERIES, stroke: token("canvas"), strokeWidth: 2 }}
                        />
                      </LineChart>
                    ) : chartType === 'area' ? (
                      <AreaChart data={chartData} margin={{ top: 4, right: 0, bottom: 0, left: 0 }}>
                        <defs>
                          <linearGradient id="priceGradient" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor={SERIES} stopOpacity={0.25}/>
                            <stop offset="100%" stopColor={SERIES} stopOpacity={0}/>
                          </linearGradient>
                        </defs>
                        <CartesianGrid stroke="rgba(255,255,255,0.05)" vertical={false} />
                        <XAxis
                          dataKey="timestamp"
                          tickFormatter={formatChartTime}
                          stroke={token("chart-axis")}
                          tick={{ fontSize: 10, fontFamily: 'JetBrains Mono' }}
                          tickLine={false}
                          axisLine={false}
                          interval="equidistantPreserveStart"
                          minTickGap={48}
                        />
                        <YAxis
                          stroke={token("chart-axis")}
                          tick={{ fontSize: 10, fontFamily: 'JetBrains Mono' }}
                          tickLine={false}
                          axisLine={false}
                          width={64}
                          domain={['auto', 'auto']}
                          tickFormatter={(value) => value.toFixed(2)}
                        />
                        <Tooltip
                          contentStyle={{
                            backgroundColor: token("surface-raised"),
                            border: '1px solid rgba(255,255,255,0.1)',
                            borderRadius: '10px',
                            fontSize: '12px',
                            fontFamily: 'JetBrains Mono',
                          }}
                          labelStyle={{ color: token("chart-axis"), marginBottom: '2px' }}
                          labelFormatter={(label) => new Date(label).toLocaleString('tr-TR')}
                          formatter={(value: number | undefined) => [formatPrice(value ?? 0, selectedSymbol), selectedName]}
                        />
                        <Area
                          type="monotone"
                          dataKey="price"
                          stroke={SERIES}
                          strokeWidth={2}
                          fill="url(#priceGradient)"
                          dot={false}
                          activeDot={{ r: 4, fill: SERIES, stroke: token("canvas"), strokeWidth: 2 }}
                        />
                      </AreaChart>
                    ) : (
                      /* Baseline chart - Area with reference line at opening price */
                      <AreaChart data={chartData} margin={{ top: 4, right: 0, bottom: 0, left: 0 }}>
                        <defs>
                          {/* Split at the opening price: gain above, loss below. */}
                          <linearGradient id="baselineGradientDashboard" x1="0" y1="0" x2="0" y2="1">
                            <stop offset={0} stopColor={token('up-400')} stopOpacity={0.28} />
                            <stop offset={baselineSplit} stopColor={token('up-400')} stopOpacity={0.02} />
                            <stop offset={baselineSplit} stopColor={token('down-400')} stopOpacity={0.02} />
                            <stop offset={1} stopColor={token('down-400')} stopOpacity={0.28} />
                          </linearGradient>
                          <linearGradient id="baselineStrokeDashboard" x1="0" y1="0" x2="0" y2="1">
                            <stop offset={0} stopColor={token('up-400')} />
                            <stop offset={baselineSplit} stopColor={token('up-400')} />
                            <stop offset={baselineSplit} stopColor={token('down-400')} />
                            <stop offset={1} stopColor={token('down-400')} />
                          </linearGradient>
                        </defs>
                        <CartesianGrid stroke="rgba(255,255,255,0.05)" vertical={false} />
                        <XAxis
                          dataKey="timestamp"
                          tickFormatter={formatChartTime}
                          stroke={token("chart-axis")}
                          tick={{ fontSize: 10, fontFamily: 'JetBrains Mono' }}
                          tickLine={false}
                          axisLine={false}
                          interval="equidistantPreserveStart"
                          minTickGap={48}
                        />
                        <YAxis
                          stroke={token("chart-axis")}
                          tick={{ fontSize: 10, fontFamily: 'JetBrains Mono' }}
                          tickLine={false}
                          axisLine={false}
                          width={64}
                          domain={['auto', 'auto']}
                          tickFormatter={(value) => value.toFixed(2)}
                        />
                        <Tooltip
                          contentStyle={{
                            backgroundColor: token("surface-raised"),
                            border: '1px solid rgba(255,255,255,0.1)',
                            borderRadius: '10px',
                            fontSize: '12px',
                            fontFamily: 'JetBrains Mono',
                          }}
                          labelStyle={{ color: token("chart-axis"), marginBottom: '2px' }}
                          labelFormatter={(label) => new Date(label).toLocaleString('tr-TR')}
                          formatter={(value: number | undefined) => [formatPrice(value ?? 0, selectedSymbol), selectedName]}
                        />
                        <ReferenceLine
                          y={chartData[0]?.price}
                          stroke={token("chart-axis")}
                          strokeDasharray="5 5"
                          label={{ value: 'Açılış', fill: token("chart-label"), fontSize: 11 }}
                        />
                        <Area
                          type="monotone"
                          dataKey="price"
                          stroke="url(#baselineStrokeDashboard)"
                          strokeWidth={2}
                          fill="url(#baselineGradientDashboard)"
                          dot={false}
                          activeDot={{ r: 4, fill: SERIES, stroke: token("canvas"), strokeWidth: 2 }}
                        />
                      </AreaChart>
                    )}
                  </ResponsiveContainer>
                ) : (
                  <div className="h-72 flex items-center justify-center text-gray-600 text-sm">
                    Bu sembol için grafik verisi bulunamadı
                  </div>
                )}
              </div>
            </section>

            {/* Market overview */}
            <section>
              <SectionHeader title="Piyasa Özeti" subtitle="Kartlara tıklayarak grafiği değiştirin" />
              {loading ? (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  {[...Array(8)].map((_, i) => (
                    <div key={i} className="card p-3.5 animate-pulse">
                      <div className="flex items-center gap-2.5 mb-3">
                        <div className="w-8 h-8 rounded-lg bg-white/[0.06]"></div>
                        <div className="h-3 bg-white/[0.06] rounded w-16"></div>
                      </div>
                      <div className="h-5 bg-white/[0.06] rounded w-24 mb-2"></div>
                      <div className="h-4 bg-white/[0.06] rounded w-12"></div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  {marketData.map((item) => (
                    <MarketCard
                      key={item.symbol}
                      icon={getIcon(item.symbol)}
                      label={item.name}
                      price={formatPrice(item.price, item.symbol)}
                      changePercent={item.changePercent}
                      sparkline={sparklines[item.symbol]}
                      accent={isGold(item.symbol) ? 'gold' : 'neutral'}
                      selected={selectedSymbol === item.symbol}
                      onClick={() => handleSelect(item.symbol, item.name)}
                      onDetail={() => handleDetailClick(item.symbol)}
                    />
                  ))}
                </div>
              )}
            </section>

            {/* Gold */}
            {turkishGold.length > 0 && (
              <section>
                <SectionHeader
                  title="Altın"
                  subtitle="Gram, çeyrek, yarım ve tam altın (TL)"
                  action={
                    <button onClick={() => navigate('/altin')} className="text-xs font-medium text-accent-400 hover:text-accent-300 transition-colors">
                      Tümü →
                    </button>
                  }
                />
                <MarketStrip
                  entries={goldEntries}
                  accent="gold"
                  selectedKey={selectedSymbol}
                  onSelect={(key) => {
                    const g = turkishGold.find((x) => x.symbol === key);
                    handleSelect(key, g?.name);
                  }}
                />
              </section>
            )}

            {/* Indices */}
            {indicesData.length > 0 && (
              <section>
                <SectionHeader title="Endeksler" subtitle="Dünya borsalarından anlık veriler" />
                <MarketStrip
                  entries={indexEntries}
                  accent="neutral"
                  selectedKey={selectedSymbol}
                  onSelect={(key) => {
                    const idx = indicesData.find(d => d.symbol === key);
                    if (idx) handleSelect(idx.symbol, idx.name);
                  }}
                />
              </section>
            )}

            {/* Economic calendar teaser */}
            <section>
              <div
                onClick={() => navigate('/ekonomik-takvim')}
                className="card card-hover p-4 group"
              >
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-3 min-w-0">
                    <span className="icon-tile"><Calendar className="w-4 h-4 text-accent-400" /></span>
                    <div className="min-w-0">
                      <h3 className="text-sm font-medium text-white">Ekonomik Takvim</h3>
                      <p className="text-xs text-gray-500 mt-0.5 truncate">TCMB, FED, ECB kararları · NFP, CPI, PMI verileri</p>
                    </div>
                  </div>
                  <span className="text-gray-600 group-hover:text-accent-300 group-hover:translate-x-0.5 transition-all flex-shrink-0">→</span>
                </div>
              </div>
            </section>

            {/* Bottom trio */}
            <section className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <CurrencyConverter marketData={marketData} />

              <div className="card p-4">
                <h3 className="section-heading mb-1">Banka Kurları</h3>
                <p className="section-sub mb-3">Güncel alış-satış kurları</p>
                <ul>
                  {BANKS.map((bank) => (
                    <li key={bank.name}>
                      <a
                        href={bank.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="group flex items-center justify-between py-1.5 text-[13px] text-gray-400 hover:text-white transition-colors"
                      >
                        {bank.name}
                        <span className="text-gray-700 group-hover:text-accent-300 transition-colors">↗</span>
                      </a>
                    </li>
                  ))}
                </ul>
              </div>

              <LatestNews />
            </section>
          </div>

          {/* Right rail */}
          <aside className="lg:col-span-4">
            <div className="lg:sticky lg:top-36 space-y-4">
              {loading ? (
                <div className="card p-2 animate-pulse space-y-1">
                  {[...Array(4)].map((_, i) => (
                    <div key={i} className="flex justify-between px-2.5 py-2.5">
                      <div className="h-3 bg-white/[0.06] rounded w-24"></div>
                      <div className="h-3 bg-white/[0.06] rounded w-14"></div>
                    </div>
                  ))}
                </div>
              ) : stockStats && (
                <div className="card p-2">
                  <div className="px-2.5 pt-1.5 pb-2">
                    <span className="text-xs font-semibold text-gray-400">Hisseler</span>
                  </div>
                  <StatTile icon={<BarChart3 className="w-4 h-4 text-accent-400" />} label="BIST Hisseleri" value={stockStats.total_bist_stocks} onClick={() => navigate('/bist')} />
                  <StatTile icon={<Activity className="w-4 h-4 text-accent-alt-400" />} label="US Hisseleri" value={stockStats.total_us_stocks} onClick={() => navigate('/us-stocks')} />
                  <StatTile icon={<Building2 className="w-4 h-4 text-gray-400" />} label="Tüm Hisseler" value={stockStats.total_bist_stocks + stockStats.total_us_stocks} onClick={() => navigate('/all-stocks')} />
                  <StatTile
                    icon={<Coins className="w-4 h-4 text-gold-400" />}
                    label="Son Veri"
                    value={stockStats.latest_data_date ? new Date(stockStats.latest_data_date).toLocaleDateString('tr-TR') : '—'}
                  />
                </div>
              )}
              <MarketMovers market="bist" />
              <ForumHighlights />
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
};

export default ImprovedDashboard;
